import { defineComponent, h, ref, onMounted } from "vue";
import { useRepositories } from "@/infra/api/injects";
import { LocalizationError, Errors } from "@/error/error";
import { CommunicationState } from "@/type";
import { useRouter, useRoute } from "vue-router";

export interface PasswordResetConfirmArgs {
  newPassword: string;
  confirmPassword: string;
}

const PasswordResetConfirmContainer = (WrappedComponent: any) =>
  defineComponent({
    name: "PasswordResetConfirmContainer",
    setup() {
      const error = ref<LocalizationError | null>(null);
      const communicationState = ref<CommunicationState>(1);
      const token = ref<string>("");
      const isTokenValid = ref<boolean>(true);
      const repositories = useRepositories();
      const router = useRouter();
      const route = useRoute();

      onMounted(async () => {
        token.value = (route.query.token as string) || "";

        if (!token.value) {
          isTokenValid.value = false;
          error.value = new Errors().InvalidToken;
          return;
        }

        // トークンの有効性を検証
        try {
          const valid = await repositories.value.PasswordResetRepository.verifyPasswordResetToken({
            token: token.value,
          });
          isTokenValid.value = valid;
          if (!valid) {
            error.value = new Errors().InvalidToken;
          }
        } catch (e) {
          isTokenValid.value = false;
          error.value = new Errors().InvalidToken;
        }
      });

      async function confirmPasswordReset(req: PasswordResetConfirmArgs): Promise<void> {
        try {
          error.value = null;
          communicationState.value = 2;

          await repositories.value.PasswordResetRepository.confirmPasswordReset({
            token: token.value,
            new_password: req.newPassword,
          });

          // 成功したら完了画面へ遷移
          router.push("/password-reset/complete");
        } catch (e) {
          if (e instanceof LocalizationError) {
            error.value = e;
          }
        } finally {
          communicationState.value = 1;
        }
      }

      return {
        confirmPasswordReset,
        error,
        communicationState,
        isTokenValid,
      };
    },
    render() {
      return h(WrappedComponent, {
        communicationState: this.communicationState,
        localizationError: this.error,
        isTokenValid: this.isTokenValid,
        onConfirmPasswordReset: (request: PasswordResetConfirmArgs) => this.confirmPasswordReset(request),
      });
    },
  });

export default PasswordResetConfirmContainer;
